import React, { useEffect, useRef, useState } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials as mockTestimonials, heroImages } from '../mock';
import api from '../lib/api';

const Testimonials = () => {
  const [items, setItems] = useState([]);
  const [avg, setAvg] = useState(null);
  const scroller = useRef(null);

  useEffect(() => {
    api.get('/testimonials')
      .then((res) => {
        const list = res.data?.testimonials || [];
        setItems(list.length ? list : mockTestimonials);
        setAvg(res.data?.average_rating);
      })
      .catch(() => setItems(mockTestimonials));
  }, []);

  const scrollBy = (dir) => {
    if (scroller.current) scroller.current.scrollBy({ left: dir * 380, behavior: 'smooth' });
  };

  return (
    <section id="testimonials" className="relative py-20 md:py-28 bg-cream overflow-hidden" data-testid="testimonials-section">
      <div className="absolute inset-0 opacity-[0.08] bg-cover bg-center pointer-events-none" style={{ backgroundImage: `url(${heroImages.nature})` }} />
      <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full flex items-center justify-center bg-rose-soft">
                <span className="w-3 h-3 rounded-full bg-mahogany" />
              </span>
              <span className="eyebrow">Voices Along the Path</span>
            </div>
            <h2 className="font-serif-display text-burgundy text-4xl md:text-5xl leading-[1.05]">
              What Our Guests Discovered
              <span className="block">on Their <span className="font-italic-display text-rose-italic">Healing Journey</span></span>
            </h2>
            {avg != null && (
              <div className="mt-4 flex items-center gap-2" data-testid="testimonials-average">
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="w-4 h-4" fill={s < Math.round(avg) ? '#A44758' : 'none'} stroke="#A44758" />
                  ))}
                </div>
                <span className="text-mahogany/80 text-sm">{avg} average from {items.length} reviews</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => scrollBy(-1)} aria-label="Prev" data-testid="testimonials-prev" className="w-11 h-11 rounded-full bg-rose-soft flex items-center justify-center hover:bg-rose-deep transition-colors">
              <ChevronLeft className="w-5 h-5 text-mahogany" />
            </button>
            <button onClick={() => scrollBy(1)} aria-label="Next" data-testid="testimonials-next" className="w-11 h-11 rounded-full bg-rose-soft flex items-center justify-center hover:bg-rose-deep transition-colors">
              <ChevronRight className="w-5 h-5 text-mahogany" />
            </button>
          </div>
        </div>

        <div ref={scroller} data-testid="testimonials-scroller"
          className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth"
          style={{ scrollbarWidth: 'thin' }}>
          {items.map((t, i) => (
            <article key={t.id || i}
              className="snap-start shrink-0 w-[320px] md:w-[380px] bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-rose/40"
              data-testid={`testimonial-card-${i}`}>
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating || 5 }).map((_, s) => (
                  <Star key={s} className="w-4 h-4" fill="#A44758" stroke="#A44758" />
                ))}
                <span className="ml-2 text-sm text-mahogany/80">{t.rating || 5}/5</span>
              </div>
              <p className="font-serif-display text-burgundy text-lg md:text-xl leading-snug line-clamp-6">
                &ldquo;{t.text}&rdquo;
              </p>
              <div className="mt-5 pt-5 border-t border-rose/40 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rose-soft flex items-center justify-center text-mahogany font-serif-display overflow-hidden">
                  {t.avatar_url ? <img src={t.avatar_url} alt={t.name} className="w-full h-full object-cover" /> : (t.name || '?').charAt(0)}
                </div>
                <div>
                  <div className="text-burgundy font-medium">{t.name}</div>
                  {t.program && <div className="text-sm text-mahogany/70">{t.program}</div>}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
